/* eslint-disable max-len */
import { useDraggable } from '@dnd-kit/core';
import { AnimatePresence, Variants, motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'wouter';
import { useAppEvent } from '../../../CodesThatsGottaGoSomewhereElse';
import { BoardLiteData } from '../../../api/ApiTypes';
import { MyBoardData } from '../../../api/endpoints/boards';
import { Icon } from '../../../concepts/resources/Icon';
import { MaybeLink } from '../../../ui/MaybeLink';
import { useResolve } from '../../Container';
import { BoardItem } from '../../common/BoardItem';
import { SortMode } from '../../common/Sorter';
import { noticePopupWith } from '../../popups/OptionsPopup';
import { PopupManager } from '../../popups/PopupManager';
import { LiveBoardInfo } from '../elements/BoardParticipantPreview';
import { useBoardThumb } from '../elements/BoardThumb';
import { NewBoardActionMenuButton } from './NewBoardActionMenuButton';

const notInTeamPopup = noticePopupWith(
    'This board is not in a team!',
    'This board is not owned by a team, and therefore is open to anyone in your organization who has the board link. ' +
    'We recommend moving this board into one of your teams.  You can do this by clicking the 3 dot menu and selecting Move.'
);

const useNotInTeamPopup = () => {
    const popups = useResolve(PopupManager);
    return () => popups.invoke(notInTeamPopup);
};

const nextAnimation: Variants = {
    before: {
        opacity: 0,
    },
    in: {
        opacity: 1,
        y: '0rem',
        transition: {
            duration: .2,
            ease: 'easeOut',
        }
    },
    out: {
        opacity: 0,
        scale: 0.5,
        transition: {
            duration: .2,
            ease: 'easeOut',
        }
    }
};

export type OnSelect = (board: BoardLiteData) => void;

export const BoardListContext = React.createContext<{ onSelect?: OnSelect, draggable?: boolean, sort?: SortMode, showWorkspace?: boolean }>({});

export const BoardListItem = React.memo<{ board: MyBoardData }>((props) => {

    const [board, setBoard] = useState(props.board);
    const [deleted, setDeleted] = useState(false);
    const thumbUrl = useBoardThumb(board);
    const { onSelect, draggable, sort, showWorkspace } = useContext(BoardListContext);
    const onClick = !!onSelect ? () => onSelect(board) : null;
    const onNotInTeam = useNotInTeamPopup();

    useAppEvent('board:patch', e => {
        if (e.board.id === props.board.id) {
            delete e.board.createdAt;
            setBoard(s => ({ ...s, ...e.board }));
        }
    });
    useAppEvent('board:move', e => {
        if (e.boardId === props.board.id) {
            // setDeleted(true);
        }
    });
    useAppEvent('board:delete', e => {
        if (e.boardId === props.board.id) {
            setDeleted(true);
        }
    });
    useEffect(() => {
        setBoard(props.board);
    }, [props.board]);

    const dateMode = (
        (sort?.[0] === 'lastSeenAt' ? 'lastSeenAt' : null) ??
        (sort?.[0] === 'updatedAt' ? 'updatedAt' : null) ??
        'createdAt'
    );

    const { attributes, listeners, setNodeRef } = useDraggable({
        id: `board:${board.id}`,
        data: board,
        disabled: !draggable,
    });

    const boardPath = [
        `workspace:${board.workspaceId}`,
        board.folderId ? `folder:${board.folderId}` : null,
        `board:${board.id}`,
    ].filter(x => !!x).join('/');

    return <AnimatePresence>
        {!deleted &&
            <motion.div variants={nextAnimation} initial="before" animate="in" exit="out">
                <BoardItem ref={setNodeRef} clickable {...listeners} {...attributes}>
                    <BoardItem.Header>
                        <BoardItem.Label title={board.label}>
                            {board.label}
                        </BoardItem.Label>
                        <BoardItem.ActionMenu>
                            <NewBoardActionMenuButton boardId={board.id} />
                        </BoardItem.ActionMenu >
                    </BoardItem.Header >
                    <MaybeLink to={`~/${board.id}`} onClick={onClick}>
                        <BoardItem.Main>
                            <BoardItem.Image>
                                <BoardItem.Thumb src={thumbUrl} />
                            </BoardItem.Image>
                            <LiveBoardInfo path={boardPath} />
                        </BoardItem.Main>
                    </MaybeLink>
                    <BoardItem.Footer>
                        {showWorkspace && <>
                            {board.workspaceId &&
                                <Link to={`~/${board.workspaceId}`}>
                                    <BoardItem.Link>
                                        <span>{board.workspaceLabel}</span>
                                    </BoardItem.Link>
                                </Link>
                            }
                            {!board.workspaceId &&
                                <span onClick={onNotInTeam}>
                                    <BoardItem.Link warning>
                                        <Icon src="deqo://icons/hero/exclamation-circle" size="1" />
                                        <span>Not in a team</span>
                                    </BoardItem.Link>
                                </span>
                            }
                        </>}
                        <BoardItem.Date>
                            {dateMode === 'lastSeenAt' && <span>
                                {board.lastSeenAt ? `Visited ${board.lastSeenAt.toLocaleDateString()}` : ''}
                            </span>}
                            {dateMode === 'updatedAt' && <span>
                                Updated {board.updatedAt?.toLocaleDateString()}
                            </span>}
                            {dateMode === 'createdAt' && <span>
                                Created {board.createdAt?.toLocaleDateString()}
                            </span>}
                        </BoardItem.Date>
                    </BoardItem.Footer>
                </BoardItem >
            </motion.div >
        }
    </AnimatePresence >;
});
